<template>
  <ZSDialog
    v-model:visible="visible"
    :title="t('moodle_wizard.title')"
    width="min(880px, 96vw)"
  >
    <div class="wizard">
      <div class="wizard-steps">
        <span :class="['wizard-step', { active: step === 1, done: step > 1 }]">
          1. {{ t('moodle_wizard.step_create') }}
        </span>
        <span class="wizard-step-sep">›</span>
        <span :class="['wizard-step', { active: step === 2 }]">
          2. {{ t('moodle_wizard.step_enrol') }}
        </span>
      </div>

      <!-- Step 1: create user -->
      <div v-if="step === 1" class="wizard-body">
        <p class="wizard-hint">{{ t('moodle_users.prefill_description') }}</p>

        <div class="row">
          <div class="col-12 col-md-4">
            <ZSInput v-model="form.firstName" :label="t('moodle_users.prefill_fields.first_name')" />
          </div>
          <div class="col-12 col-md-4">
            <ZSInput v-model="form.lastName" :label="t('moodle_users.prefill_fields.last_name')" />
          </div>
          <div class="col-12 col-md-4">
            <ZSInput v-model="form.contactEmail" :label="t('moodle_users.prefill_fields.contact_email')" />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-4">
            <ZSInput v-model="form.username" :label="t('moodle_users.prefill_fields.username')" />
          </div>
          <div class="col-12 col-md-4">
            <ZSInput v-model="form.password" type="password" :label="t('moodle_users.prefill_fields.password')" />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-4">
            <ZSSelect
              v-model="form.zs_profile"
              :label="t('moodle_users.cf.zs_profile')"
              :items="zsProfileOptions"
              item-label="label"
              item-value="value"
              clear
            />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-4">
            <ZSInput v-model="form.partnerName" :label="t('moodle_users.prefill_fields.partner_name')" disabled />
          </div>
          <div class="col-12 col-md-4">
            <ZSInput v-model="form.partnerTax" :label="t('moodle_users.prefill_fields.partner_tax')" disabled />
          </div>
          <div class="col-12 col-md-4">
            <ZSInput v-model="form.partnerEmail" :label="t('moodle_users.prefill_fields.partner_email')" disabled />
          </div>
        </div>
      </div>

      <!-- Step 2: enrol -->
      <div v-else class="wizard-body wizard-body--scroll">
        <p class="wizard-hint">
          {{ t('moodle_wizard.enrol_hint', { name: createdUsername }) }}
        </p>

        <ZSTree
          :data="categoryTree"
          item-key="nodeKey"
          item-value="id"
          :item-label="treeItemLabel"
          selectable
          multiselect
          show-checkboxes
          initially-expanded
          :is-selectable="(node) => node.type === 'course'"
          v-model="selectedCourseIds"
          border
          :empty-text="categoriesLoading ? t('moodle_categories.courses_loading') : t('moodle_categories.no_results')"
        >
          <template #default="{ node }">
            <div class="moodle-tree-node">
              <span v-if="node.type === 'category'" class="moodle-tree-name">{{ node.name || '-' }}</span>
              <span v-else class="moodle-tree-name">{{ node.fullname || node.shortname || '-' }}</span>
            </div>
          </template>
        </ZSTree>

        <p class="wizard-selected">{{ t('moodle_wizard.selected_count', { count: selectedCourseIds.length }) }}</p>
      </div>
    </div>

    <template #footer>
      <template v-if="step === 1">
        <ZSButton class="text" color="danger" icon="Close" @click="close">
          {{ t('general.cancel') }}
        </ZSButton>
        <ZSButton color="primary" icon="Checkmark" :loading="createLoading" @click="createUser">
          {{ t('moodle_wizard.create_and_continue') }}
        </ZSButton>
      </template>
      <template v-else>
        <ZSButton class="text" icon="Close" @click="close">
          {{ t('moodle_wizard.skip_close') }}
        </ZSButton>
        <ZSButton
          color="primary"
          icon="Checkmark"
          :loading="enrolLoading"
          :disabled="!selectedCourseIds.length"
          @click="enrol"
        >
          {{ t('moodle_wizard.enrol') }}
        </ZSButton>
      </template>
    </template>
  </ZSDialog>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useZSToast } from '@/components/zs-components/toast/useToast';
import { useUserStore } from '@/stores/user';
import {
  useMoodleUser,
  buildPrefillCustomFields,
  getErrorMessage,
  zsProfileOptions,
} from '@/composables/useMoodleUser';

const { t } = useI18n();
const { add: addToast } = useZSToast();
const userStore = useUserStore();
const {
  partner,
  moodleService,
  buildPrefillDefaults,
  buildPrefillPayload,
  checkExistingMoodleUser,
} = useMoodleUser();

// Errors surface as toasts (the app-wide default), not inline boxes.
function notifyWarn(detail) {
  addToast({ position: 'tr', summary: t('general.warning'), detail, type: 'warn', life: 4000 });
}
function notifyError(detail) {
  addToast({ position: 'tr', summary: t('general.error'), detail, type: 'error', life: 5000 });
}

const visible = ref(false);
const step = ref(1);

const form = ref(buildPrefillDefaults());
const createLoading = ref(false);
const createdUserId = ref(null);
const createdUsername = ref('');

const categoryTree = ref([]);
const categoriesLoading = ref(false);
const selectedCourseIds = ref([]);
const enrolLoading = ref(false);

// --- open/close/reset ---
function resetState() {
  step.value = 1;
  form.value = buildPrefillDefaults();
  createLoading.value = false;
  createdUserId.value = null;
  createdUsername.value = '';
  categoryTree.value = [];
  categoriesLoading.value = false;
  selectedCourseIds.value = [];
  enrolLoading.value = false;
}

async function open() {
  resetState();
  if (!partner.value?.codigo) {
    await userStore.getPartner();
    form.value = buildPrefillDefaults();
  }
  visible.value = true;
}

function close() {
  visible.value = false;
}

// Reset when the dialog is dismissed (e.g. via the close button).
watch(visible, (val) => {
  if (!val) resetState();
});

defineExpose({ open, close });

// --- step 1: create user ---
async function createUser() {
  const { payload, error } = buildPrefillPayload(form.value);
  if (error) {
    notifyWarn(error);
    return;
  }

  const customfields = buildPrefillCustomFields(form.value);
  if (customfields.length > 0) {
    payload.customfields = customfields;
  }

  createLoading.value = true;
  const existing = await checkExistingMoodleUser({ email: payload.email, username: payload.username });
  if (existing.error) {
    notifyWarn(existing.error);
    createLoading.value = false;
    return;
  }

  try {
    const response = await moodleService.createUsers(userStore.token, [payload]);
    const created = response?.data ?? [];
    const id = created[0]?.id;
    if (!id) {
      notifyError(t('moodle_wizard.no_user_id'));
      createLoading.value = false;
      return;
    }
    createdUserId.value = id;
    createdUsername.value = created[0]?.username || payload.username;
    addToast({ position: 'tr', summary: t('general.success'), detail: t('moodle_users.create_success', { count: 1 }), type: 'success', life: 4000 });
    step.value = 2;
    await loadCategories();
  } catch (err) {
    notifyError(getErrorMessage(err, t('moodle_users.create_failed')));
  } finally {
    createLoading.value = false;
  }
}

// --- step 2: category tree (duplicated tree logic) ---
function treeItemLabel(node) {
  if (node.type === 'course') return node.fullname || node.shortname || '';
  return node.name || '';
}

function makeCategoryNode(cat) {
  return { ...cat, type: 'category', nodeKey: `cat-${cat.id}`, children: [], hasChildren: false };
}

// Eager tree: categories nested by parent with their courses attached as leaves,
// so the whole tree can be shown expanded (categories + courses) at once.
// The backend only returns visible categories/courses (visible == 1).
function buildCategoryTree(categories, courses) {
  const nodes = new Map();
  categories.forEach((cat) => nodes.set(cat.id, makeCategoryNode(cat)));

  const roots = [];
  nodes.forEach((node) => {
    const parentNode = node.parent ? nodes.get(node.parent) : null;
    if (parentNode) parentNode.children.push(node);
    else roots.push(node);
  });

  courses.forEach((course) => {
    const catNode = nodes.get(course.categoryid);
    if (!catNode) return;
    catNode.children.push({
      ...course,
      type: 'course',
      nodeKey: `course-${course.categoryid}-${course.id}`,
    });
  });

  nodes.forEach((node) => {
    node.hasChildren = node.children.length > 0;
  });

  return roots;
}

async function loadCategories() {
  categoriesLoading.value = true;
  try {
    const [catResponse, courseResponse] = await Promise.all([
      moodleService.getCategories(userStore.token, '', ''),
      moodleService.getCourses(userStore.token, '', ''),
    ]);
    categoryTree.value = buildCategoryTree(
      catResponse?.data ?? [],
      courseResponse?.data?.courses ?? [],
    );
  } catch (err) {
    categoryTree.value = [];
    notifyError(getErrorMessage(err, t('moodle_categories.search_failed')));
  } finally {
    categoriesLoading.value = false;
  }
}

// --- step 2: enrol ---
async function enrol() {
  if (!selectedCourseIds.value.length || !createdUserId.value) return;
  enrolLoading.value = true;
  try {
    await moodleService.enrolUser(userStore.token, createdUserId.value, [...selectedCourseIds.value]);
    addToast({
      position: 'tr',
      summary: t('general.success'),
      detail: t('moodle_wizard.enrol_success', { count: selectedCourseIds.value.length }),
      type: 'success',
      life: 4000,
    });
    close();
  } catch (err) {
    notifyError(getErrorMessage(err, t('moodle_wizard.enrol_failed')));
  } finally {
    enrolLoading.value = false;
  }
}
</script>

<style scoped>
.wizard {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.wizard-steps {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.wizard-step {
  color: var(--text-color-secondary, #888);
}

.wizard-step.active {
  color: var(--color-orange-alt, #d35400);
  font-weight: 600;
}

.wizard-step.done {
  color: var(--color-green, #2e7d32);
}

.wizard-step-sep {
  color: var(--text-color-secondary, #888);
}

.wizard-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-right: 4px;
}

/* Only the enrol step (course tree) needs to scroll. Step 1 stays overflow-free
   so opening a ZSSelect dropdown doesn't trigger a vertical scrollbar. */
.wizard-body--scroll {
  max-height: 74vh;
  overflow-y: auto;
}

.wizard-hint {
  margin: 0 0 0.25rem;
  color: var(--text-color-secondary, #666);
}

.moodle-tree-node {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.moodle-tree-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.moodle-tree-hint {
  font-style: italic;
  color: var(--text-color-secondary, #888);
}

.moodle-tree-error {
  color: var(--color-danger, #d9534f);
}

.wizard-selected {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: var(--text-color-secondary, #666);
}
</style>
